// src/JourneyFinalSummary.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JourneyService from "./services/JourneyService";
import { getBlueprintForProblem } from "./JourneyBlueprints";
import { getProblemByKey } from "./journeyProblems";
import { dirForLang, normalizeLang } from "./i18n";
import { getSystemStrings } from "./strings/systemStrings";
import { useAuth } from "./auth/AuthContext";
import { resolveUserFacingText } from "./utils/userFacingText";
import {
  buildUnifiedEmotionalEchoBody,
  formatMirrorSummaryAsFlowingBlock,
} from "./utils/journeyMirrorText";
import "./JourneyFinalSummary.css";

function useQuery() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  const problemKey = params.get("problemKey") || "rel_breakup_fresh_pain";
  const runId = String(params.get("runId") || "").trim();
  return { lang, problemKey, runId, params, location };
}

export default function JourneyFinalSummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang, problemKey, runId, location } = useQuery();
  const dir = dirForLang(lang);
  const t = getSystemStrings(lang, user?.gender).journeySummary;

  const [intake, setIntake] = useState(null);
  const [entries, setEntries] = useState([]);
  const [dayStates, setDayStates] = useState({});
  const blueprint = useMemo(() => getBlueprintForProblem(problemKey, lang), [problemKey, lang]);
  const problemMeta = useMemo(() => getProblemByKey(problemKey), [problemKey]);

  useEffect(() => {
    if (runId) {
      JourneyService.setActiveRun(lang, problemKey, runId);
    }
    const { intake: intakeData } = JourneyService.loadIntake(lang, problemKey, { runId });
    const { entries: entryData } = JourneyService.getFullJournal(lang, problemKey, { runId });
    const { days } = JourneyService.loadAllDayStates(lang, problemKey, undefined, { runId });
    setIntake(intakeData || null);
    setEntries(entryData || []);
    setDayStates(days || {});
  }, [lang, problemKey, runId]);

  const finalDay = blueprint?.durationDays || blueprint?.totalDays || problemMeta?.defaultDays || 7;
  const finalSummaryBody = useMemo(() => {
    const state = dayStates[finalDay]?.journeyV2 || {};
    const explicit =
      state.exerciseAnswers?.day18FinalSummary ||
      state.dnaPocV2?.answers?.day18FinalSummary ||
      "";
    if (String(explicit || "").trim()) {
      return formatMirrorSummaryAsFlowingBlock(String(explicit).trim(), {
        lang,
        maxChars: 1400,
      });
    }
    const dayEntries = entries.filter(
      (entry) => Number(entry?.dayNumber ?? entry?.day ?? 0) === Number(finalDay)
    );
    return buildUnifiedEmotionalEchoBody(dayEntries, lang, {
      maxParts: 6,
      maxTotalChars: 1200,
    });
  }, [dayStates, finalDay, entries, lang]);

  const groupedEntries = useMemo(() => {
    const byDay = new Map();
    entries.forEach((entry) => {
      const dayNumber = Number(entry?.dayNumber ?? entry?.day ?? 0);
      if (!dayNumber) return;
      if (!byDay.has(dayNumber)) byDay.set(dayNumber, []);
      byDay.get(dayNumber).push(entry);
    });
    return Array.from(byDay.entries()).sort((a, b) => a[0] - b[0]);
  }, [entries]);

  const openOverview = () => {
    const sp = new URLSearchParams(location.search);
    sp.set("lang", lang);
    sp.set("problemKey", problemKey);
    navigate({ pathname: "/journey/overview", search: sp.toString() });
  };

  const openJournal = () => {
    const sp = new URLSearchParams(location.search);
    sp.set("lang", lang);
    sp.set("problemKey", problemKey);
    navigate({ pathname: "/journey/journal", search: sp.toString() });
  };

  return (
    <div className="journey-summary-page" dir={dir}>
      <header className="journey-summary-header">
        <div>
          <h1>{t.title}</h1>
          <p className="journey-summary-subtitle">{t.subtitle}</p>
          {blueprint?.title && <p className="journey-summary-problem">{blueprint.title}</p>}
        </div>
        <div className="journey-summary-actions">
          <button type="button" className="summary-secondary" onClick={openJournal}>
            {t.openJournal}
          </button>
          <button type="button" className="summary-primary" onClick={openOverview}>
            {t.backToOverview}
          </button>
        </div>
      </header>

      <section className="summary-card">
        <h2>{t.intakeHeader}</h2>
        {intake ? (
          <div className="summary-intake-grid">
            <div>
              <div className="summary-label">{t.intakePain}</div>
              <div className="summary-value">{intake.painNow || "-"}</div>
            </div>
            <div>
              <div className="summary-label">{t.intakeGoal}</div>
              <div className="summary-value">{intake.desiredEndState || "-"}</div>
            </div>
            <div>
              <div className="summary-label">{t.intakeWord}</div>
              <div className="summary-value">{intake.oneWord || "-"}</div>
            </div>
          </div>
        ) : (
          <p className="summary-empty">-</p>
        )}
      </section>

      <section className="summary-card">
        <h2>{t.finalSummaryHeader}</h2>
        {finalSummaryBody ? (
          <p className="summary-value summary-final-flow">{finalSummaryBody}</p>
        ) : (
          <p className="summary-value">{t.noFinalSummary}</p>
        )}
      </section>

      <section className="summary-card">
        <h2>{t.journalHeader}</h2>
        {!entries.length && <p className="summary-empty">{t.journalEmpty}</p>}
        {groupedEntries.map(([dayNumber, dayEntries]) => {
          const daySubtitle = resolveUserFacingText(blueprint?.daySubtitles?.[dayNumber], {
            lang,
            gender: user?.gender,
            fallback: "",
          });
          return (
            <div key={`summary-day-${dayNumber}`} className="summary-day-entry">
              <div className="summary-day-title">
                {t.dayLabel} {dayNumber}
              </div>
              {daySubtitle && <div className="summary-day-sub">{daySubtitle}</div>}
              {dayEntries.map((entry, idx) => (
                <div key={`summary-step-${dayNumber}-${entry.questionId || idx}`} className="summary-step">
                  {entry.questionText && <div className="summary-day-note">{entry.questionText}</div>}
                  <div className="summary-step-flow">
                    {formatMirrorSummaryAsFlowingBlock(String(entry.text || ""), {
                      lang,
                      maxChars: 4000,
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </section>
    </div>
  );
}
